import { formatDate } from '@/helpers/dates';
import { Badge, Box, Button } from '@palmetto/palmetto-components';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  AssetIncentiveAllocation,
  Clientize,
  IncentiveAllocationStatus,
  IncentiveAllocationStatusLabels,
} from 'types';

import { SkeletonBox } from '../SkeletonBox';

import { EditAllocationModal } from './EditAllocationModal';

interface AllocationsSidebarProps {
  allocations?: Clientize<AssetIncentiveAllocation>[];
  isLoading?: boolean;
}

const getStatusBadgeVariant = (status: IncentiveAllocationStatus): 'success' | 'warning' | 'default' | 'info' => {
  switch (status) {
    case IncentiveAllocationStatus.completed:
      return 'success';
    case IncentiveAllocationStatus.pending:
      return 'info';
    case IncentiveAllocationStatus.cancelled:
      return 'default';
    default:
      return 'default';
  }
};

const getAllocationFilter = (allocationName: string) =>
  `[{"id": "allocation", "operation": "Equal", "selectedValues": ["${allocationName}"]}]`;

export const AllocationsSidebar = ({ allocations, isLoading }: AllocationsSidebarProps) => {
  const { key } = useParams<{ key: string }>();
  const [searchParams] = useSearchParams();
  const [editingAllocation, setEditingAllocation] = useState<Clientize<AssetIncentiveAllocation> | null>(null);

  const currentFilters = searchParams.get('advancedFilters');
  let selectedAllocationName: string | null = null;

  if (currentFilters) {
    try {
      const parsed = JSON.parse(currentFilters);
      const allocationFilter = parsed.find((f: any) => f.id === 'allocation');
      if (allocationFilter?.selectedValues?.length > 0) {
        selectedAllocationName = allocationFilter.selectedValues[0];
      }
    } catch {
      // Invalid JSON, ignore
    }
  }

  if (isLoading) {
    return (
      <Box childGap="lg" radius="md" borderWidth="xs" borderColor="separator" padding="lg">
        <Box as="h3" fontWeight="medium" fontSize="md">
          Allocations
        </Box>
        <Box childGap="sm">
          <SkeletonBox height="60px" width="100%" />
          <SkeletonBox height="60px" width="100%" />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box childGap="lg" radius="md" borderWidth="xs" borderColor="separator" padding="lg">
        <Box as="h3" fontWeight="medium" fontSize="md">
          Allocations
        </Box>
        <Box>
          {allocations && allocations.length > 0 ? (
            allocations.map((allocation, index) => {
              const isSelected = selectedAllocationName === allocation.name;

              return (
                <Box padding="0 0 sm 0" key={allocation.id}>
                  <Box>
                    <Box direction="row" alignItems="baseline" justifyContent="space-between" padding="0 0 xs 0">
                      <Box as="h3" fontWeight="medium" fontSize="sm">
                        <Link
                          to={`/admin/incentives/${key}?advancedFilters=${encodeURIComponent(getAllocationFilter(allocation.name))}`}
                          style={{ fontWeight: isSelected ? 700 : 500 }}
                        >
                          {allocation.name}
                        </Link>
                      </Box>
                      <Button
                        onClick={() => setEditingAllocation(allocation)}
                        variant="primary"
                        tone="neutral"
                        size="xs"
                        aria-label="Edit Allocation"
                      >
                        Edit
                      </Button>
                    </Box>
                    <Box direction="row" alignItems="baseline" justifyContent="space-between" padding="xs 0">
                      <Badge
                        variant={getStatusBadgeVariant(allocation.status)}
                        message={IncentiveAllocationStatusLabels[allocation.status]}
                        size="sm"
                      />
                    </Box>
                    <Box fontSize="xs" color="body-secondary" padding="xs 0 0 0">
                      <Box>Allocation Date: {formatDate(allocation.allocationDate)}</Box>
                      <Box>Last Edited: {formatDate(allocation.lastEditedDate)}</Box>
                    </Box>
                    {index < allocations.length - 1 && (
                      <Box borderWidth="0 0 xs 0" borderColor="separator" margin="sm 0 0 0" />
                    )}
                  </Box>
                </Box>
              );
            })
          ) : (
            <Box padding="md" textAlign="center" color="body-secondary" fontSize="sm">
              No allocations yet
            </Box>
          )}
        </Box>
      </Box>

      {editingAllocation && (
        <EditAllocationModal
          isOpen={!!editingAllocation}
          onClose={() => setEditingAllocation(null)}
          allocation={editingAllocation}
          incentiveKey={key!}
        />
      )}
    </>
  );
};
